import { useCallback } from 'react';

import type { User } from '@core/types/api/User/User';
import { useSlabMutation } from '@web/hooks/useSlabMutation';
import { useConfirmDialog } from '@web/providers/ConfirmProvider/useConfirmDialog';

interface UseConfirmToggleUserActivationParams {
  tenantIdOverride?: string;
}

interface UseConfirmToggleUserActivationReturn {
  /** Opens a confirm dialog and, on confirm, enables/disables the user. */
  confirmAndToggle(user: User): void;
  /** True while an activate/deactivate request is in flight. */
  isLoading: boolean;
}

/**
 * Encapsulates the confirm dialog plus the activate/deactivate mutations for a
 * user, mirroring how `useArchiveManager` wraps the archive flow. When
 * `tenantIdOverride` is provided the admin-scoped routes are used.
 */
export const useConfirmToggleUserActivation = ({
  tenantIdOverride,
}: UseConfirmToggleUserActivationParams): UseConfirmToggleUserActivationReturn => {
  const confirm = useConfirmDialog();

  const isTenantScoped = tenantIdOverride !== undefined;
  const activateUser = useSlabMutation(
    isTenantScoped ? 'POST user activate as admin' : 'POST user activate',
  );
  const deactivateUser = useSlabMutation(
    isTenantScoped ? 'POST user deactivate as admin' : 'POST user deactivate',
  );

  const confirmAndToggle = useCallback(
    (user: User): void => {
      const isDisabling = !user.isDeactivated;
      const toggle = isDisabling ? deactivateUser : activateUser;

      confirm.alert({
        title: isDisabling ? 'Confirm disable' : 'Confirm enable',
        description: isDisabling
          ? 'Are you sure you want to disable this user'
          : 'Are you sure you want to enable this user',
        actions: [
          { label: 'Cancel', variant: isDisabling ? 'slab-primary' : 'slab-secondary' },
          {
            label: isDisabling ? 'Disable' : 'Enable',
            variant: isDisabling ? 'slab-secondary' : 'slab-primary',
            onClick: (): void => {
              toggle.mutate({
                args: { pathParams: { id: user.id, tenantID: tenantIdOverride }, body: {} },
              });
            },
          },
        ],
      });
    },
    [confirm, activateUser, deactivateUser, tenantIdOverride],
  );

  return {
    confirmAndToggle,
    isLoading: activateUser.isPending || deactivateUser.isPending,
  };
};
